import React, { useEffect, useRef } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useOverviewContext } from '@context/OverviewContext';
import { isTeacherAllowedPath } from '@/utils/teacherRouteUtils';

const RequiredSecureAuth: React.FC = () => {
  const { authState, oktaAuth } = useOktaAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { students, teacherSchools, userRoleVal, loading, error, identityId } = useOverviewContext();

  const retryCountRef = useRef(0);
  const maxRetries = 3;

  useEffect(() => {
    if (authState && !authState.isAuthenticated) {
      oktaAuth.signInWithRedirect();
    }
  }, [authState, oktaAuth]);

  useEffect(() => {
    if (!authState?.isAuthenticated || loading) return;

    if (error) {
      navigate('/error', { state: { errorType: 'USER_API', identityId }, replace: true });
      return;
    }

    // Profile still loading — avoid treating teachers as "no students" and hitting /error.
    if (!userRoleVal) return;

    if (userRoleVal === 'TEACHER') {
      const multiSchool = (teacherSchools?.length ?? 0) > 1;
      if (location.pathname === '/' && multiSchool) {
        navigate('/school-selection', { replace: true });
        return;
      }
      if (location.pathname === '/' && !multiSchool) {
        navigate('/overview', { replace: true });
        return;
      }
      if (location.pathname === '/school-selection' && !multiSchool) {
        navigate('/overview', { replace: true });
        return;
      }
      if (!isTeacherAllowedPath(location.pathname)) {
        navigate('/overview', { replace: true });
      }
      return;
    }

    if (students && students.length > 0) {
      retryCountRef.current = 0;
      if (location.pathname === '/') {
        const hasMultipleStudents = students.length > 1;
        const isLearningCoach = userRoleVal === 'LEARNING_COACH';

        if (isLearningCoach && hasMultipleStudents) {
          navigate('/learning-coach', { replace: true });
        } else {
          navigate('/overview', { replace: true });
        }
      }
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const retry = () => {
      if (cancelled) return;

      if (error) {
        navigate('/error', { state: { errorType: 'USER_API', identityId }, replace: true });
        return;
      }

      if (userRoleVal === 'TEACHER') {
        navigate('/overview', { replace: true });
        return;
      }

      if (students && students.length > 0) {
        retryCountRef.current = 0;
        if (location.pathname === '/') {
          const hasMultipleStudents = students.length > 1;
          const isLearningCoach = userRoleVal === 'LEARNING_COACH';

          if (isLearningCoach && hasMultipleStudents) {
            navigate('/learning-coach', { replace: true });
          } else {
            navigate('/overview', { replace: true });
          }
        }
        return;
      }

      if (retryCountRef.current >= maxRetries) {
        navigate('/error', { state: { errorType: 'USER_API', identityId }, replace: true });
        return;
      }

      retryCountRef.current += 1;
      timer = setTimeout(retry, 3000);
    };

    timer = setTimeout(retry, 3000);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [authState, loading, students, teacherSchools, error, identityId, navigate, location.pathname, userRoleVal]);

  const isTeacher = userRoleVal === 'TEACHER';
  const awaitingProfile = Boolean(authState?.isAuthenticated) && !loading && !error && !userRoleVal;
  const awaitingTeacherStudents =
    isTeacher && !loading && !error && (!students || students.length === 0);

  if (
    authState === null ||
    !authState.isAuthenticated ||
    loading ||
    awaitingProfile ||
    awaitingTeacherStudents ||
    (!isTeacher && (!students || students.length === 0))
  ) {
    return renderLoader(
      !authState || !authState.isAuthenticated
        ? 'Redirecting to login...'
        : 'Please wait while we are accessing your account...'
    );
  }

  return <Outlet />;
};

function renderLoader(message: string) {
  return (
    <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '100vh' }}>
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">{message}</span>
      </div>
      <div className="mt-3 text-primary fw-medium body-m">{message}</div>
    </div>
  );
}

export default RequiredSecureAuth;
